/**
 * Serviço de cache para otimizar requisições de API
 * Evita múltiplas chamadas desnecessárias para os mesmos dados
 */

interface CacheItem<T> {
  data: T;
  timestamp: number;
  ttl: number; // Time to live em milissegundos
}

class CacheService {
  private cache = new Map<string, CacheItem<any>>();
  private defaultTTL = 5 * 60 * 1000; // 5 minutos por padrão

  /**
   * Armazenar dados no cache
   */
  set<T>(key: string, data: T, ttl?: number): void {
    const item: CacheItem<T> = {
      data,
      timestamp: Date.now(),
      ttl: ttl || this.defaultTTL
    };
    
    this.cache.set(key, item);
  }

  /**
   * Recuperar dados do cache
   */
  get<T>(key: string): T | null {
    const item = this.cache.get(key);
    
    if (!item) {
      return null;
    }

    // Verificar se o item expirou
    const now = Date.now();
    if (now - item.timestamp > item.ttl) {
      this.cache.delete(key);
      return null;
    }

    return item.data;
  }

  /**
   * Verificar se existe no cache
   */
  has(key: string): boolean {
    const item = this.cache.get(key);
    if (!item) return false;

    const now = Date.now();
    if (now - item.timestamp > item.ttl) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }

  /**
   * Remover item do cache
   */
  delete(key: string): boolean {
    const deleted = this.cache.delete(key);
    if (deleted) {
      console.log(`💾 Cache DELETE: ${key}`);
    }
    return deleted;
  }

  /**
   * Limpar cache expirado
   */
  cleanExpired(): void {
    const now = Date.now();
    let cleaned = 0;

    // Usar Array.from para compatibilidade com versões mais antigas do TypeScript
    const entries = Array.from(this.cache.entries());
    
    for (const [key, item] of entries) {
      if (now - item.timestamp > item.ttl) {
        this.cache.delete(key);
        cleaned++;
      }
    }

    if (cleaned > 0) {
      console.log(`💾 Cache CLEANED: ${cleaned} itens expirados removidos`);
    }
  }

  /**
   * Limpar todo o cache
   */
  clear(): void {
    this.cache.clear();
    console.log(`💾 Cache CLEARED: todos os itens removidos`);
  }

  /**
   * Obter estatísticas do cache
   */
  getStats() {
    const now = Date.now();
    let active = 0;
    let expired = 0;

    // Usar Array.from para compatibilidade com versões mais antigas do TypeScript
    const items = Array.from(this.cache.values());
    
    for (const item of items) {
      if (now - item.timestamp > item.ttl) {
        expired++;
      } else {
        active++;
      }
    }

    return {
      total: this.cache.size,
      active,
      expired
    };
  }

  /**
   * Gerar chave de cache baseada em parâmetros
   */
  generateKey(prefix: string, params: Record<string, any> = {}): string {
    const sortedParams = Object.keys(params)
      .sort()
      .map(key => `${key}:${params[key]}`)
      .join('|');
    
    return `${prefix}:${sortedParams}`;
  }
}

// Instância singleton
export const cacheService = new CacheService();

// Limpar cache expirado a cada 5 minutos
setInterval(() => {
  cacheService.cleanExpired();
}, 5 * 60 * 1000);

export default cacheService;
